import React from 'react';
import { DocumentTextIcon } from '@heroicons/react/24/outline';

const TransactionLogs: React.FC = () => {
  return (
    <div className="section-card">
      <h1 className="section-title">Transaction Logs</h1>
      <p className="section-description">
        View and manage transaction logs, track payment attempts, and maintain a history of payment activities.
      </p>

      <div className="mt-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">
          Features
        </h2>
        <div className="space-y-4">
          <div className="feature-item">
            <DocumentTextIcon className="feature-icon" />
            <div className="feature-text">
              <h3 className="feature-title">Log Management</h3>
              <p className="feature-description">
                Handle transaction logs with the following capabilities:
              </p>
              <ul className="list-disc list-inside mt-2 text-gray-600">
                <li>View detailed transaction history</li>
                <li>Delete individual logs</li>
                <li>Bulk delete multiple logs</li>
                <li>Track payment attempts (limited to 3 per order)</li>
              </ul>
            </div>
          </div>

          <div className="feature-item">
            <DocumentTextIcon className="feature-icon" />
            <div className="feature-text">
              <h3 className="feature-title">Log Details</h3>
              <p className="feature-description">
                Comprehensive log information management:
              </p>
              <ul className="list-disc list-inside mt-2 text-gray-600">
                <li>Order selection (with event and user details)</li>
                <li>Payment reference ID (unique)</li>
                <li>Status tracking (Pending, Confirmed, Rejected)</li>
                <li>Admin notes field</li>
                <li>Confirmation timestamp</li>
              </ul>
            </div>
          </div>

          <div className="feature-item">
            <DocumentTextIcon className="feature-icon" />
            <div className="feature-text">
              <h3 className="feature-title">Log List</h3>
              <p className="feature-description">
                View and manage logs in a table format with the following columns:
              </p>
              <ul className="list-disc list-inside mt-2 text-gray-600">
                <li>Event title (searchable)</li>
                <li>User name (searchable)</li>
                <li>Payment reference (searchable)</li>
                <li>Status (color-coded badges)</li>
                <li>Confirmation date (sortable)</li>
                <li>Creation date (sortable)</li>
              </ul>
            </div>
          </div>

          <div className="feature-item">
            <DocumentTextIcon className="feature-icon" />
            <div className="feature-text">
              <h3 className="feature-title">Status Tracking</h3>
              <p className="feature-description">
                Monitor and filter transaction log status:
              </p>
              <ul className="list-disc list-inside mt-2 text-gray-600">
                <li>Pending (yellow badge)</li>
                <li>Confirmed (green badge)</li>
                <li>Rejected (red badge)</li>
                <li>Filter logs by status</li>
                <li>View chronological order of attempts</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">
          Best Practices
        </h2>
        <div className="bg-blue-50 p-4 rounded-lg">
          <ul className="list-disc list-inside space-y-2 text-blue-800">
            <li>Regularly review transaction logs for patterns</li>
            <li>Monitor failed payment attempts</li>
            <li>Keep accurate records of payment status changes</li>
            <li>Use status filters to analyze payment trends</li>
            <li>Maintain clear documentation of payment issues</li>
            <li>Track payment attempt limits per order</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TransactionLogs; 